'use client';

import clsx from 'clsx';
import { ChangeEventHandler, FC, useCallback, useRef } from 'react';

import { getInputAria } from '../Input.helpers';
import { InputBaseWithOptions, InputGroupValue, InputOption } from '../Input.type';

type Props<InputValue = any> = Prettify<Omit<InputBaseWithOptions<InputValue>, 'variant'>>;

export const ControlToggleButtonInputGroup: FC<Props> = ({ onChange, onBlur, ...props }) => {
  const changeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (onChange) {
        onChange(event.target.checked ? event.target.value : '');
      }
    },
    [props.value, onChange],
  );

  return (
    <fieldset className={'flex gap-2'} data-testid={props['data-testid']}>
      {props.options.map((option) => (
        <ControlToggleButtonInput
          {...props}
          option={option}
          onChange={changeHandler}
          onBlur={onBlur}
          key={option.value}
        />
      ))}
    </fieldset>
  );
};

type ControlToggleButtonInputProps = Prettify<
  Omit<InputBaseWithOptions<InputGroupValue>, 'options' | 'onChange' | 'variant'> & {
    option: InputOption;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onBlur?: () => void;
  }
>;

/** Component representing a radio toggle button */
const ControlToggleButtonInput: FC<ControlToggleButtonInputProps> = ({ option, onChange, onBlur, ...props }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const checked = isChecked(option.value, props.value);

  return (
    <label
      className={clsx(
        'relative flex flex-1 cursor-pointer items-center justify-center overflow-hidden rounded-4xl border p-2 font-semibold transition-colors duration-100 outline-none hover:bg-sta-sec-hov focus-visible:bg-sta-sec-hov has-focus-visible:bg-sta-sec-hov',
        {
          'border-bor-neu-mut bg-bac-tie1 hover:border-for-pri-bas focus-visible:border-for-pri-bas active:bg-for-pri-lig has-focus-visible:border-for-pri-bas':
            !checked,
          'border-2 border-for-pri-bas bg-for-pri-lig active:bg-sta-sec-pre': checked,
        },
        props.inputClassName,
      )}
      data-testid={
        option['data-testid'] || (props['data-testid'] ? `${props['data-testid']}-${option.value}` : undefined)
      }
    >
      <input
        type="radio"
        name={props.name}
        value={option.value}
        className="absolute top-0 left-0 opacity-0"
        {...getInputAria(props)}
        checked={checked}
        ref={inputRef}
        onChange={onChange}
        onBlur={onBlur}
      />
      <div className="c-fs-body2 text-tex-def-tie1 transition-colors duration-100">{option.label}</div>
    </label>
  );
};

const isChecked = (value: string, inputValue: InputGroupValue | undefined): boolean =>
  (Array.isArray(inputValue) && inputValue.includes(value)) || value === inputValue;
